import {AbstractInteraction, DrawIconOptions, InteractionState} from "./abstract-interaction";
import {Feature, Position} from "geojson";
import {GeometryUtil} from "../geometry";
import {SnapMode, SnapPoint} from "../snap";
import {DrawFeature} from "@mapbox/mapbox-gl-draw";
import {MapEvent} from "../../document/model/map";

export class TransformInteractionState extends InteractionState {
  public dragPath: string | undefined;
  public center: Position | undefined;
}

export abstract class AbstractTransform<StateType extends TransformInteractionState> extends AbstractInteraction<StateType> {
  protected feature: Feature;
  protected originalFeature: DrawFeature;
  protected transformedFeature: DrawFeature;


  protected vertexMarkers: any[] = [];
  protected centerMarker;

  protected text: Feature | undefined;

  isManualInputSupported(state: StateType): boolean {
    return true;
  }

  buildInitialState(initialState: StateType): StateType {
    if (this.feature && initialState?.featureID != this.feature?.id?.toString()) {
      this.controller.interaction.historyClear();
    }

    if (initialState.features?.size) {
      this.feature = initialState.features.get(initialState.featureID);
      this.originalFeature = this.createFeature(this.feature);
      this.transformedFeature = this.createFeature(this.feature);

      this.displayDrawFeature(this.originalFeature);
      this.displayDrawFeature(this.transformedFeature);
    }

    let state: StateType = {
      ...super.buildInitialState(initialState),
      dragPath: undefined,
      center: initialState.center ?? (this._hasCenter() ? GeometryUtil.centerOfMass(this.feature) : undefined),
    };

    if (initialState.features?.size) {
      this._generateVertexMarkers(this.feature, 'map-move');
      this.vertexMarkers.forEach(marker => this.removeDrawFeature(marker.id.toString()))
      this.vertexMarkers = this._generateVertexMarkers(this.feature, this.getVertexIcon());
      this._generateCenterMarker(state)
    }

    return state;
  }

  getSupportedSnapModes(): SnapMode[] {
    return ['snap', 'precise']
  }

  canSave(state: StateType): boolean {
    return state.isDirty;
  }

  useHistory(state: StateType): boolean {
    return true;
  }

  onStop(state: any) {
    this.clearDrawFeatures();
  }

  save(state: StateType, nextInteraction: string | undefined = undefined) {
    this.controller.mapInstance.fire(MapEvent.LayerGeometryUpdate, {
      layerGeometry: this.controller.interaction.getLayerGeometry(state.featureID),
      feature: {
        type: 'Feature',
        geometry: (this.transformedFeature.toGeoJSON() as Feature).geometry,
        id: state?.featureID,
        //properties: { id: state.featureID ,...(state.features.get(state.featureID).properties || {})}
        properties: state.features.get(state.featureID).properties
      },
      nextInteraction
    });
  }

  async onMouseDown(state: any, e: mapboxgl.MapMouseEvent) {
    if (!this.feature) {
      return;
    }
    let scaleMarker = this._isMarker(e, this.getVertexIcon());
    if (scaleMarker) {
      this.controller.mapInstance.dragPan.disable();
      state.dragPath = scaleMarker.properties.coord_path;
    } else if (this._isMarker(e, 'auto-aim')) {
      this.controller.mapInstance.dragPan.disable();
      state.dragPath = 'center';
    }
  }

  async onMouseUp(state: StateType, e: mapboxgl.MapMouseEvent) {
    if (!state.dragPath) {
      return;
    }
    this.controller.interaction.historyPush(state);

    this.controller.mapInstance.dragPan.enable();
    await this.onDrag(state, e);
    state.features.get(state.featureID).geometry = JSON.parse(JSON.stringify((this.transformedFeature.toGeoJSON() as Feature).geometry));
    state.dragPath = undefined;
    this.tick();
  }

  async onDrag(state: any, e: mapboxgl.MapMouseEvent | mapboxgl.MapTouchEvent): Promise<void> {
    let snapPoint = this.controller.snapUtility.getSnapPoint(e);
    if (!state.dragPath) {
      return;
    }
    if (state.dragPath === 'center') {
      state.center = snapPoint.point;
      this._generateCenterMarker(state);
      return;
    }

    this.transform(state, e, snapPoint);

    this.displayDrawFeature(this.transformedFeature);

    this._generateVertexMarkers(this.transformedFeature.toGeoJSON() as Feature, this.getVertexIcon())

    if(this.text && this.text.id){
      this.removeDrawFeature(this.text.id.toString());
      this._generateCenterMarker(state);
    }
  }

  async onTouchStart(state: any, e: mapboxgl.MapTouchEvent): Promise<void> {
    //this.controller.mapInstance.getCanvas().style.pointerEvents = 'none'
    this.onMouseDown(state, e)
  }

  async onTouchEnd(state: any, e: mapboxgl.MapTouchEvent): Promise<void> {
    this.onMouseUp(state, e)
    //this.controller.mapInstance.getCanvas().style.pointerEvents = 'autor'
  }


  public abstract transform(state: TransformInteractionState, e: mapboxgl.MapMouseEvent | mapboxgl.MapTouchEvent, snapPoint: SnapPoint);

  public abstract getVertexIcon(): string;

  public getVertexSize(): number {
    return 0.5;
  }

  protected _hasCenter(): boolean {
    return false;
  }

  protected _isMarker(e: mapboxgl.MapMouseEvent | mapboxgl.MapTouchEvent, icon: string): Feature | undefined {
    let features = this.controller.mapInstance.queryRenderedFeatures(e.point);
    return features.find((feature => {
      return feature.properties.icon === icon;
    }));
  }

  protected _generateVertexMarkers(feature: Feature, icon: string, opts: Partial<DrawIconOptions> = {}): Feature[] {
    let markers = [];
    opts = {
      "icon-size": this.getVertexSize(),
      ...opts
    };

    GeometryUtil.forEachPosition(
      feature,
      (currentCoord: Position, coordIndex: number, geometryIndex: number, multiFeatureIndex: number) => {
        let path = `${coordIndex}`;
        if (['Polygon', 'MultiPolygon'].indexOf(feature.geometry.type) >= 0) {
          path = `${geometryIndex}.${path}`
        }
        if (feature.geometry.type.startsWith('Multi')) {
          path = `${multiFeatureIndex}.${path}`;
        }

        if (!feature.id && feature.properties?.id)
          feature.id = feature.properties?.id

        let draggingMarket = this.createIcon(currentCoord, icon, {
          parent: feature.id.toString(),
          coord_path: path,
          id: `${feature.id}_${path}`,
          ...opts
        });
        markers.push(draggingMarket);
        this.displayDrawFeature(draggingMarket);
      }
    );

    return markers;
  }

  getDistanceFromLatLonInKm(lat1, lon1, lat2, lon2) {
    const R = 6371; // Radius of the earth in km
    const dLat = this.deg2rad(lat2 - lat1);
    const dLon = this.deg2rad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c; // Distance in km
    return distance;
  }

  deg2rad(deg) {
    return deg * (Math.PI / 180);
  }

  protected _generateCenterMarker(state) {
    if (!this._hasCenter()) {
      return;
    }

    let isCircle = false;
    state.features.forEach((feature)=>{
      if(feature?.customProperties?.meta_type=="circle"){
        isCircle = true;
      }
    })

    if(isCircle){
      let radiusInKm = this.getDistanceFromLatLonInKm(state.center[1], state.center[0], this.transformedFeature.coordinates[0][0][1],this.transformedFeature.coordinates[0][0][0]);

      this.text = this.createLabel([state.center[0], state.center[1]], `${new Number(radiusInKm).toFixed(2)}km`, {id: 'tmp_text'})

      this.displayDrawFeature(this.text);

      const poli = this.createPolygon([[state.center[0], state.center[1]], [this.transformedFeature.coordinates[0][0][0],this.transformedFeature.coordinates[0][0][1]]], 'tmp_polygon');
      this.displayDrawFeature(poli);

      return;
    }
    this.centerMarker = this.createIcon(state.center, 'auto-aim', {
      id: 'center-marker',
      parent: this.feature.id.toString()
    });
    this.displayDrawFeature(this.centerMarker);
  }
}
